import type {
  ClaudeProject,
  ClaudeSession,
  ClaudeMessage,
  MessagePage,
  SessionTokenStats,
  ProjectStatsSummary,
  PaginationOptions,
} from "@anthropic/shared";
import { API_ENDPOINTS, DEFAULT_PAGE_SIZE } from "@anthropic/shared";

/**
 * HTTP API Client for Web version
 * Replaces Tauri IPC calls with HTTP fetch requests
 */

const API_BASE = import.meta.env.VITE_API_URL || "";
const TOKEN_KEY = "claude_history_token";

/**
 * Get auth token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Build headers with optional auth token
 */
function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  const token = getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    headers: getHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Get the Claude folder path
 */
export async function getClaudeFolderPath(): Promise<string> {
  const result = await fetchJson<{ path: string }>("/api/claude-path");
  return result.path;
}

/**
 * Validate Claude folder path
 */
export async function validateClaudeFolder(_path: string): Promise<boolean> {
  try {
    const result = await fetchJson<{ status: string; isValid: boolean }>(
      API_ENDPOINTS.HEALTH
    );
    return result.isValid;
  } catch {
    return false;
  }
}

/**
 * Scan all projects
 */
export async function scanProjects(
  _claudePath: string
): Promise<ClaudeProject[]> {
  return fetchJson<ClaudeProject[]>(API_ENDPOINTS.PROJECTS);
}

/**
 * Load sessions for a project
 */
export async function loadProjectSessions(
  projectPath: string,
  excludeSidechain?: boolean
): Promise<ClaudeSession[]> {
  // Extract project name from path
  const projectName = projectPath.split("/").pop() || projectPath;
  const url = `${API_ENDPOINTS.PROJECT_SESSIONS(projectName)}${
    excludeSidechain ? "?excludeSidechain=true" : ""
  }`;
  return fetchJson<ClaudeSession[]>(url);
}

/**
 * Load all messages from a session
 */
export async function loadSessionMessages(
  sessionPath: string
): Promise<ClaudeMessage[]> {
  const encodedPath = encodeURIComponent(sessionPath);
  return fetchJson<ClaudeMessage[]>(
    API_ENDPOINTS.SESSION_MESSAGES(encodedPath)
  );
}

/**
 * Load messages with pagination
 */
export async function loadSessionMessagesPaginated(
  sessionPath: string,
  options: PaginationOptions = {}
): Promise<MessagePage> {
  const {
    offset = 0,
    limit = DEFAULT_PAGE_SIZE,
    excludeSidechain = false,
    order = "asc" // 默认从最老消息开始
  } = options;
  const encodedPath = encodeURIComponent(sessionPath);
  const params = new URLSearchParams({
    offset: String(offset),
    limit: String(limit),
    order,
    ...(excludeSidechain && { excludeSidechain: "true" }),
  });
  return fetchJson<MessagePage>(
    `${API_ENDPOINTS.SESSION_MESSAGES(encodedPath)}?${params}`
  );
}

/**
 * Get message count for a session
 */
export async function getSessionMessageCount(
  sessionPath: string,
  excludeSidechain?: boolean
): Promise<number> {
  const encodedPath = encodeURIComponent(sessionPath);
  const url = `${API_ENDPOINTS.SESSION_COUNT(encodedPath)}${
    excludeSidechain ? "?excludeSidechain=true" : ""
  }`;
  const result = await fetchJson<{ count: number }>(url);
  return result.count;
}

/**
 * Search messages
 */
export async function searchMessages(
  _claudePath: string,
  query: string,
  _filters?: Record<string, unknown>
): Promise<ClaudeMessage[]> {
  const params = new URLSearchParams({ q: query });
  return fetchJson<ClaudeMessage[]>(`${API_ENDPOINTS.SEARCH}?${params}`);
}

/**
 * Get session token statistics
 */
export async function getSessionTokenStats(
  sessionPath: string
): Promise<SessionTokenStats> {
  const encodedPath = encodeURIComponent(sessionPath);
  return fetchJson<SessionTokenStats>(API_ENDPOINTS.STATS_SESSION(encodedPath));
}

/**
 * Get project token statistics
 */
export async function getProjectTokenStats(
  projectName: string
): Promise<SessionTokenStats[]> {
  return fetchJson<SessionTokenStats[]>(API_ENDPOINTS.STATS_PROJECT(projectName));
}

/**
 * Get project statistics summary
 */
export async function getProjectStatsSummary(
  projectName: string
): Promise<ProjectStatsSummary> {
  return fetchJson<ProjectStatsSummary>(API_ENDPOINTS.STATS_SUMMARY(projectName));
}

/**
 * Health check
 */
export async function healthCheck(): Promise<{
  status: string;
  claudePath: string;
  isValid: boolean;
  authEnabled: boolean;
}> {
  return fetchJson(API_ENDPOINTS.HEALTH);
}
